import {
  OktonPageHeader,
  OktonInput,
  OktonAlert,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, Landmark, X } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  BANCO_SICOOB,
  BANCOS_EXTRATO_API,
  BancoExtratoApi,
} from '../../../services/hooks/useContasBancarias';
import { useConsultarExtratoApi } from '../../../services/hooks/useExtratoBancarioMutations';

// O que a tela de Conciliação manda junto ao abrir esta. Sem a Conta não há o
// que consultar: ela vem escolhida de lá, junto com o banco dela (que decide
// qual API é chamada); esta tela só pede o período.
export interface EstadoConsultarExtratoApi {
  conta: string;
  descricaoConta: string;
  banco: BancoExtratoApi;
}

const hoje = new Date();

const PERIODO_VAZIO = {
  ano: String(hoje.getFullYear()),
  mes: String(hoje.getMonth() + 1),
  diaInicial: '',
  diaFinal: '',
};

export default function ConsultarExtratoApi() {
  const history = useHistory();
  const { state } = useLocation<EstadoConsultarExtratoApi | undefined>();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const [periodo, setPeriodo] = useState({ ...PERIODO_VAZIO });
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState<{
    total: number;
    inseridos: number;
    ignorados: number;
    descartados: number;
    horaspreenchidas?: number;
  } | null>(null);

  const banco = state?.banco ?? BANCO_SICOOB;
  const nomeBanco = BANCOS_EXTRATO_API[banco];
  const consultar = useConsultarExtratoApi(banco);

  // Recarregar a página perde a Conta escolhida: ela não está na URL, e
  // consultar sem saber qual Conta seria um chute.
  if (!state || !state.conta) {
    return (
      <BankAppPage>
        <OktonPageHeader
          eyebrow="CADASTROS"
          title={`Consultar Extrato ${nomeBanco}`}
          subtitle="Extrato buscado direto na API do banco"
          onBack={() => history.push('/conciliacao')}
        />
        <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
          <Stack spacing="4" maxW="720px">
            <OktonEmptyState
              title="Conta indisponível"
              description="A Conta escolhida se perdeu. Volte à Conciliação e selecione de novo uma Conta com consulta de extrato via API."
            />
            <Flex>
              <Button
                colorScheme="blue"
                onClick={() => history.push('/conciliacao')}
              >
                Voltar para a Conciliação
              </Button>
            </Flex>
          </Stack>
        </Box>
      </BankAppPage>
    );
  }

  // Dentro das funções abaixo o TypeScript esquece que o state foi checado.
  const { conta } = state;

  function handleClose() {
    history.push('/conciliacao');
  }

  async function buscarExtrato() {
    const ano = Number(periodo.ano);
    const mes = Number(periodo.mes);
    const diaInicial = periodo.diaInicial
      ? Number(periodo.diaInicial)
      : undefined;
    const diaFinal = periodo.diaFinal ? Number(periodo.diaFinal) : undefined;
    if (!ano || ano < 2000 || ano > 2100) {
      setErro('Informe um Ano válido.');
      return;
    }
    if (!mes || mes < 1 || mes > 12) {
      setErro('Informe um Mês entre 1 e 12.');
      return;
    }
    if (diaInicial && (diaInicial < 1 || diaInicial > 31)) {
      setErro('Informe um Dia Inicial entre 1 e 31.');
      return;
    }
    if (diaFinal && (diaFinal < 1 || diaFinal > 31)) {
      setErro('Informe um Dia Final entre 1 e 31.');
      return;
    }
    if (diaInicial && diaFinal && diaInicial > diaFinal) {
      setErro('O Dia Inicial não pode ser depois do Dia Final.');
      return;
    }
    setErro('');
    try {
      const retorno = await consultar.mutateAsync({
        conta,
        ano,
        mes,
        diaInicial,
        diaFinal,
      });
      setResultado(retorno);
    } catch (err: unknown) {
      setErro(
        err instanceof Error
          ? err.message
          : `Erro ao consultar o extrato no ${nomeBanco}.`,
      );
    }
  }

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={`Consultar Extrato ${nomeBanco}`}
        subtitle={`Extrato buscado direto na API do banco — ${state.descricaoConta}`}
        onBack={() => history.push('/conciliacao')}
      />

      <Box
        bg={bg}
        mt="4"
        mb="6"
        borderWidth="1px"
        rounded="2xl"
        overflow="hidden"
      >
        {!resultado ? (
          <Stack spacing="4" p="6" maxW="640px">
            <Flex align="center" gap="2" color="gray.500" fontSize="sm">
              <Landmark size={18} />
              <Text>Período da consulta no {nomeBanco}</Text>
            </Flex>

            {erro && <OktonAlert status="error" description={erro} />}

            <SimpleGrid columns={2} spacing="4">
              <Box>
                <Text
                  as="label"
                  htmlFor="consultar-extrato-ano"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Ano *
                </Text>
                <OktonInput
                  name="consultar-extrato-ano"
                  type="number"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  value={periodo.ano}
                  onChange={e => {
                    const valor = e.target.value;
                    setPeriodo(p => ({ ...p, ano: valor }));
                  }}
                />
              </Box>
              <Box>
                <Text
                  as="label"
                  htmlFor="consultar-extrato-mes"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Mês *
                </Text>
                <OktonInput
                  name="consultar-extrato-mes"
                  type="number"
                  min={1}
                  max={12}
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  value={periodo.mes}
                  onChange={e => {
                    const valor = e.target.value;
                    setPeriodo(p => ({ ...p, mes: valor }));
                  }}
                />
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing="4">
              <Box>
                <Text
                  as="label"
                  htmlFor="consultar-extrato-dia-inicial"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Dia Inicial
                </Text>
                <OktonInput
                  name="consultar-extrato-dia-inicial"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="Mês inteiro"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  value={periodo.diaInicial}
                  onChange={e => {
                    const valor = e.target.value;
                    setPeriodo(p => ({ ...p, diaInicial: valor }));
                  }}
                />
              </Box>
              <Box>
                <Text
                  as="label"
                  htmlFor="consultar-extrato-dia-final"
                  display="block"
                  fontSize="sm"
                  fontWeight="medium"
                  mb="1"
                >
                  Dia Final
                </Text>
                <OktonInput
                  name="consultar-extrato-dia-final"
                  type="number"
                  min={1}
                  max={31}
                  placeholder="Mês inteiro"
                  size="lg"
                  rounded="2xl"
                  fontWeight="light"
                  color={color}
                  value={periodo.diaFinal}
                  onChange={e => {
                    const valor = e.target.value;
                    setPeriodo(p => ({ ...p, diaFinal: valor }));
                  }}
                />
              </Box>
            </SimpleGrid>

            <Text fontSize="xs" color="gray.500">
              Deixe Dia Inicial e Dia Final em branco para trazer o mês inteiro.
              Os lançamentos vêm direto da API do {nomeBanco} e entram como
              pendentes, prontos para conciliar.
            </Text>

            <Flex justify="flex-end" gridGap={2} mt="2">
              <Button variant="outline" borderWidth="2px" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={
                  consultar.isLoading ? <Spinner size="sm" /> : undefined
                }
                isDisabled={consultar.isLoading}
                onClick={buscarExtrato}
              >
                Consultar Extrato
              </Button>
            </Flex>
          </Stack>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py="12"
            px="8"
            gap="4"
            textAlign="center"
          >
            <Flex
              bg={resultado.inseridos > 0 ? 'green.50' : 'red.50'}
              p="4"
              borderRadius="full"
            >
              {resultado.inseridos > 0 ? (
                <Check size={40} color="#059669" />
              ) : (
                <X size={40} color="#dc2626" />
              )}
            </Flex>
            <Box>
              <Text fontSize="lg" fontWeight="semibold">
                {resultado.inseridos > 0
                  ? 'Consulta concluída!'
                  : 'Nenhum lançamento novo'}
              </Text>
              <Text fontSize="sm" color="gray.500" mt="1">
                {resultado.total} lançamento(s) encontrado(s) no {nomeBanco}{' '}
                para o período.{' '}
                {resultado.inseridos > 0
                  ? `${resultado.inseridos} importado(s) com sucesso.`
                  : ''}
                {resultado.ignorados > 0 &&
                  ` ${resultado.ignorados} já existia(m) e foi(ram) ignorado(s).`}
                {!!resultado.horaspreenchidas &&
                  ` ${resultado.horaspreenchidas} já existente(s) ganhou(aram) a hora informada pelo ${nomeBanco}.`}
                {resultado.descartados > 0 &&
                  ` ${resultado.descartados} veio(vieram) sem dado suficiente e foi(ram) descartado(s).`}
              </Text>
            </Box>
            <Button colorScheme="blue" mt="2" onClick={handleClose}>
              Ir para a Conciliação
            </Button>
          </Flex>
        )}
      </Box>
    </BankAppPage>
  );
}
